import React, { useState } from 'react';
import { insuranceApi } from './api';
import { CANCEL_REASONS, REJECT_REASONS, COVERAGE_OPTIONS } from './insuranceConfig';
import { InsuranceLogo, fmtMxn } from './designHelpers';
import ModalShell from './ModalShell';

export default function AdminActionsModal({ visible, onHide, action, qpId, insurance, view, toast, onDone, onRequestCorrection }) {
  if (!visible || !action) return null;

  if (action === 'modify') return <ModifyForm visible={visible} onHide={onHide} insurance={insurance} toast={toast} onDone={onDone} />;
  if (action === 'cancel') return <CancelForm visible={visible} onHide={onHide} insurance={insurance} aseguradora={view?.aseguradora} toast={toast} onDone={onDone} />;
  if (action === 'reject') return <RejectForm visible={visible} onHide={onHide} qpId={qpId} aseguradora={view?.aseguradora} toast={toast} onDone={onDone} />;
  if (action === 'reopen') return <ReopenForm visible={visible} onHide={onHide} insurance={insurance} toast={toast} onDone={onDone} onRequestCorrection={onRequestCorrection} />;
  return null;
}

function ModifyForm({ visible, onHide, insurance, toast, onDone }) {
  const beforePrice = Number(insurance?.amount) || 0;
  const beforeCoverage = insurance?.coverage_type || 'Amplia';

  const [precio, setPrecio] = useState(String(beforePrice));
  const [cobertura, setCobertura] = useState(beforeCoverage);
  const [motivo, setMotivo] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const precioChanged = Number(precio) !== beforePrice;
  const coberturaChanged = cobertura !== beforeCoverage;
  const canSave = motivo.trim().length > 5 && (precioChanged || coberturaChanged);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setArchivo({ file, nombre: file.name, size: formatBytes(file.size) });
    e.target.value = '';
  };

  const submit = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (precioChanged) fd.append('changes[amount]', String(Number(precio)));
      if (coberturaChanged) fd.append('changes[coverage_type]', cobertura);
      fd.append('reason', motivo);
      if (archivo?.file) fd.append('endorsement', archivo.file);
      const res = await insuranceApi.modify(insurance.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Póliza modificada', life: 2500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={720}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Acción administrativa</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>Modificar póliza</div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4, fontFamily: 'var(--pei-font-mono)' }}>
          {insurance?.policy_number || '—'}
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div className="banner info" style={{ marginBottom: 18 }}>
          <i className="pi pi-info-circle" />
          <div>
            <div className="b-title">Solo cambios de precio, cobertura o monto</div>
            <div className="b-sub">Para cambiar fechas, cancela la póliza y crea una nueva.</div>
          </div>
        </div>

        <div className="pei-label" style={{ marginBottom: 10 }}>¿Qué cambia?</div>
        <div style={{ border: '1px solid var(--pei-gray-5)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr',
            background: 'var(--pei-bg-soft)', padding: '10px 16px',
            borderBottom: '1px solid var(--pei-gray-5)',
            fontSize: 10.5, fontWeight: 700, color: 'var(--pei-text-label)',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            <span>Campo</span><span>Antes</span><span>Ahora</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', padding: '14px 16px', alignItems: 'center', borderBottom: '1px solid var(--pei-gray-5)' }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Precio anual</span>
            <span style={{ fontSize: 14, color: 'var(--pei-text-helper)', textDecoration: precioChanged ? 'line-through' : 'none' }}>{fmtMxn(beforePrice)}</span>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: 'var(--pei-text-helper)' }}>$</span>
              <input
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                style={{
                  width: '100%', height: 38,
                  paddingLeft: 26, paddingRight: 12,
                  border: '1.5px solid var(--pei-cyan-bright)',
                  borderRadius: 10, fontSize: 14, fontWeight: 600,
                  fontFamily: 'inherit',
                  background: precioChanged ? 'rgba(34,236,154,0.06)' : 'white',
                  outline: 'none',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', padding: '14px 16px', alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Cobertura</span>
            <span style={{ fontSize: 14, color: 'var(--pei-text-helper)', textDecoration: coberturaChanged ? 'line-through' : 'none' }}>{beforeCoverage}</span>
            <select
              value={cobertura}
              onChange={(e) => setCobertura(e.target.value)}
              style={{
                width: '100%', height: 38, padding: '0 12px',
                border: '1.5px solid var(--pei-cyan-bright)',
                borderRadius: 10, fontSize: 14, fontWeight: 500,
                fontFamily: 'inherit',
                background: coberturaChanged ? 'rgba(34,236,154,0.06)' : 'white',
                outline: 'none',
              }}>
              {COVERAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 18, marginBottom: 18 }}>
          <div className="pei-label">
            Motivo del cambio <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Explica brevemente el cambio (endoso, ajuste, reclasificación…)"
            className="pei-textarea"
            rows={3}
          />
        </div>

        <MiniDropzone label="Adjuntar endoso (opcional)" archivo={archivo} setArchivo={setArchivo} onPickFile={handleFile} />
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Cancelar</button>
        <div style={{ flex: 1 }} />
        <button
          className="pei-btn pei-btn-primary floor-shadow"
          onClick={submit}
          disabled={!canSave || submitting}
        >
          {submitting ? 'Guardando…' : 'Guardar modificación'}
        </button>
      </div>
    </ModalShell>
  );
}

function CancelForm({ visible, onHide, insurance, aseguradora, toast, onDone }) {
  const [motivoTipo, setMotivoTipo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [notas, setNotas] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOtro = motivoTipo === 'otro';
  const motivoFinal = isOtro ? motivoTexto : motivoTipo;
  const canCancel = motivoFinal.length > 0 && confirmText === 'CANCELAR' && !submitting;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setArchivo({ file, nombre: file.name, size: formatBytes(file.size) });
    e.target.value = '';
  };

  const submit = async () => {
    if (!canCancel) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('reason', motivoFinal);
      if (fecha) fd.append('effective_date', fecha);
      if (notas) fd.append('notes', notas);
      if (archivo?.file) fd.append('proof', archivo.file);
      const res = await insuranceApi.cancel(insurance.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Seguro cancelado', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={640}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6, color: 'var(--pei-danger)' }}>Acción destructiva</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>Cancelar seguro</div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          {aseguradora?.nombre} · <span style={{ fontFamily: 'var(--pei-font-mono)' }}>{insurance?.policy_number || '—'}</span>
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div className="banner danger" style={{ marginBottom: 18 }}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="b-title">El seguro quedará marcado como cancelado</div>
            <div className="b-sub">Puedes crear uno nuevo después. El histórico se conserva.</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">
            Motivo de cancelación <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <select className="pei-select" value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)}>
            <option value="">Selecciona…</option>
            {CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isOtro && (
            <input
              autoFocus
              className="pei-input"
              placeholder="Especifica el motivo…"
              value={motivoTexto}
              onChange={(e) => setMotivoTexto(e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <div className="pei-label">Fecha efectiva</div>
            <input type="date" className="pei-input" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">Notas adicionales (opcional)</div>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="pei-textarea" rows={3}
            placeholder="Cualquier información que ayude a entender el contexto…" />
        </div>

        <MiniDropzone label="Comprobante de cancelación (opcional)" archivo={archivo} setArchivo={setArchivo} onPickFile={handleFile} />

        <div style={{ marginTop: 18, padding: 16, background: 'rgba(220,38,38,0.04)', border: '1.5px solid rgba(220,38,38,0.30)', borderRadius: 12 }}>
          <div style={{ fontSize: 12.5, color: 'var(--pei-text)', fontWeight: 500, marginBottom: 8 }}>
            Para confirmar, escribe <span style={{ fontFamily: 'var(--pei-font-mono)', background: 'white', padding: '1px 8px', borderRadius: 4, fontWeight: 700 }}>CANCELAR</span> en el campo:
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="CANCELAR"
            style={{
              width: '100%', height: 40, padding: '0 14px',
              border: `1.5px solid ${confirmText === 'CANCELAR' ? 'var(--pei-danger)' : 'rgba(220,38,38,0.30)'}`,
              borderRadius: 10, fontSize: 14, fontFamily: 'var(--pei-font-mono)',
              fontWeight: 700, letterSpacing: '0.08em',
              color: 'var(--pei-danger)',
              background: 'white',
              outline: 'none',
            }}
          />
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Volver</button>
        <div style={{ flex: 1 }} />
        <button className="pei-btn pei-btn-danger" disabled={!canCancel} onClick={submit}>
          <i className="pi pi-ban" style={{ fontSize: 12 }} /> {submitting ? 'Cancelando…' : 'Cancelar seguro'}
        </button>
      </div>
    </ModalShell>
  );
}

function RejectForm({ visible, onHide, qpId, aseguradora, toast, onDone }) {
  const [motivoTipo, setMotivoTipo] = useState('');
  const [motivoTexto, setMotivoTexto] = useState('');
  const [providerName, setProviderName] = useState(aseguradora?.nombre || '');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isOtro = motivoTipo === 'otro';
  const motivoFinal = isOtro ? motivoTexto : motivoTipo;
  const canConfirm = motivoFinal.length > 2 && !submitting;

  const submit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await insuranceApi.reject(qpId, { reason: motivoFinal, notes: notas, provider_name: providerName });
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Rechazo registrado', detail: 'El paso queda disponible para reintentar', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={640}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '26px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Provider rejected</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>
          Aseguradora rechazó la solicitud
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginTop: 4 }}>
          Sin póliza emitida. El paso quedará disponible para reintentar.
        </div>
      </div>

      <div className="scrollable" style={{ padding: '0 32px 14px', flex: 1, minHeight: 0 }}>
        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">Aseguradora</div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            border: '1.5px solid var(--pei-gray-5)',
            background: 'var(--pei-bg-readonly)',
            borderRadius: 12,
          }}>
            <InsuranceLogo provider={aseguradora?.providerObj} name={providerName} size={26} />
            <input
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--pei-text)', outline: 'none' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="pei-label">
            Motivo del rechazo <span style={{ color: 'var(--pei-danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>*</span>
          </div>
          <select className="pei-select" value={motivoTipo} onChange={(e) => setMotivoTipo(e.target.value)}>
            <option value="">Selecciona…</option>
            {REJECT_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {isOtro && (
            <input autoFocus className="pei-input" placeholder="Especifica el motivo…"
              value={motivoTexto} onChange={(e) => setMotivoTexto(e.target.value)} style={{ marginTop: 8 }} />
          )}
        </div>

        <div>
          <div className="pei-label">Notas</div>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="pei-textarea" rows={3}
            placeholder="Cualquier información adicional para el histórico…" />
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={submitting}>Cancelar</button>
        <div style={{ flex: 1 }} />
        <button className="pei-btn pei-btn-primary floor-shadow" disabled={!canConfirm} onClick={submit}>
          {submitting ? 'Confirmando…' : 'Confirmar rechazo'}
        </button>
      </div>
    </ModalShell>
  );
}

function ReopenForm({ visible, onHide, insurance, toast, onDone, onRequestCorrection }) {
  const [hover, setHover] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (mode) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await insuranceApi.reopen(insurance.id, { mode });
      if (res?.data?.success) {
        if (mode === 'fix') {
          // Registramos el evento 'reopened' y abrimos el formulario de
          // corrección con los valores precargados (no reenvía correo).
          onRequestCorrection?.();
        } else {
          toast?.current?.show?.({
            severity: 'success',
            summary: 'Seguro cancelado, paso disponible',
            life: 3500,
          });
          onDone?.();
          onHide?.();
        }
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={680}>
      <button className="modal-close" onClick={onHide}><i className="pi pi-times" /></button>
      <div style={{ padding: '32px 36px 16px' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Reabrir paso</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.012em', marginBottom: 6 }}>
          ¿Qué tipo de cambio necesitas?
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)', marginBottom: 24 }}>
          Elige según lo que vayas a corregir o reemplazar.
        </div>
      </div>

      <div style={{ padding: '0 36px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ReopenCard
          icon="pi-pencil"
          iconBg="rgba(50,184,223,0.14)"
          iconColor="var(--pei-cyan)"
          title="Corrección tipográfica"
          desc="Editar el mismo seguro (póliza #, fechas, etc.). Misma row, queda registrado como `corrected`."
          chip="Hoy"
          chipBg="rgba(50,184,223,0.16)"
          chipColor="var(--pei-blue-deeper)"
          hovered={hover === 'fix'}
          onMouseEnter={() => setHover('fix')}
          onMouseLeave={() => setHover(null)}
          onClick={() => submit('fix')}
          disabled={submitting}
        />
        <ReopenCard
          icon="pi-refresh"
          iconBg="rgba(34,236,154,0.18)"
          iconColor="var(--pei-green-true)"
          title="Cambio de póliza"
          desc="Cancelar la actual y crear una nueva desde cero. El histórico de la anterior se conserva."
          chip="Crea nueva"
          chipBg="rgba(34,236,154,0.20)"
          chipColor="#0a7c4c"
          hovered={hover === 'replace'}
          onMouseEnter={() => setHover('replace')}
          onMouseLeave={() => setHover(null)}
          onClick={() => submit('replace')}
          disabled={submitting}
        />
      </div>
    </ModalShell>
  );
}

function ReopenCard({ icon, iconBg, iconColor, title, desc, chip, chipBg, chipColor, hovered, disabled, ...rest }) {
  return (
    <button {...rest} disabled={disabled} style={{
      background: hovered ? 'var(--pei-bg-soft)' : 'white',
      border: `1.5px solid ${hovered ? 'var(--pei-cyan-bright)' : 'var(--pei-gray-5)'}`,
      borderRadius: 16,
      padding: '20px 18px',
      textAlign: 'left',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12,
      opacity: disabled ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: iconBg, color: iconColor, display: 'grid', placeItems: 'center' }}>
          <i className={`pi ${icon}`} style={{ fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', background: chipBg, color: chipColor, borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {chip}
        </span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.008em' }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--pei-text-helper)', lineHeight: 1.5 }}>{desc}</div>
      <div style={{ fontSize: 12, color: 'var(--pei-cyan)', fontWeight: 600, marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        Continuar <i className="pi pi-arrow-right" style={{ fontSize: 10 }} />
      </div>
    </button>
  );
}

function MiniDropzone({ label, archivo, setArchivo, onPickFile }) {
  const inputRef = React.useRef(null);
  return (
    <div>
      <div className="pei-label">{label}</div>
      <input ref={inputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onPickFile} />
      {archivo ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px',
          background: 'white',
          border: '1px solid var(--pei-gray-5)',
          borderRadius: 12,
        }}>
          <div style={{ width: 32, height: 40, borderRadius: 4, background: '#fff5f5', border: '1px solid #fecaca', display: 'grid', placeItems: 'center', color: 'var(--pei-danger)', fontSize: 9, fontWeight: 700 }}>PDF</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pei-text)' }}>{archivo.nombre}</div>
            <div style={{ fontSize: 11, color: 'var(--pei-text-helper)' }}>{archivo.size}</div>
          </div>
          <button onClick={() => setArchivo(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--pei-text-helper)' }}>
            <i className="pi pi-times" style={{ fontSize: 13 }} />
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} type="button" style={{
          width: '100%', padding: '14px 16px',
          background: 'transparent',
          border: '1.5px dashed var(--pei-cyan-bright)',
          borderRadius: 12,
          color: 'var(--pei-cyan)', fontSize: 13, fontWeight: 500,
          cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <i className="pi pi-cloud-upload" style={{ fontSize: 14 }} />
          Arrastra o haz click para adjuntar
        </button>
      )}
    </div>
  );
}

function formatBytes(b) {
  if (!b && b !== 0) return '';
  const k = 1024;
  if (b < k) return b + ' B';
  if (b < k * k) return Math.round(b / k) + ' KB';
  return (b / (k * k)).toFixed(1) + ' MB';
}
